import React from "react";
import { motion } from "motion/react";
import {
  X,
  User,
  Mail,
  DollarSign,
  FileText,
  CheckCircle2,
  Clock,
  Calendar,
  Link as LinkIcon,
  Hash,
  Edit,
  CheckCircle,
  Loader2,
} from "lucide-react";
import FeaturePermission from "../permissions/FeaturePermission";
import { POST_PAID_PERMISSIONS } from "../../utils/permissions";
import { canApproveRequest } from "../../utils/permissions";

const RequestDetailsModal = ({ request, onClose, onApprove, onEdit, approving = false }) => {
  const getStatusBadge = () => {
    if (request.hod_approval && request.finance_approval) {
      return (
        <span className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-green-800 bg-green-100 rounded-full">
          <CheckCircle2 className="w-4 h-4" />
          Fully Approved
        </span>
      );
    } else if (request.hod_approval && !request.finance_approval) {
      return (
        <span className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-blue-800 bg-blue-100 rounded-full">
          <Clock className="w-4 h-4" />
          Pending Finance Approval
        </span>
      );
    } else {
      return (
        <span className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-orange-800 bg-orange-100 rounded-full">
          <Clock className="w-4 h-4" />
          Pending HOD Approval
        </span>
      );
    }
  };

  // Get approval tier
  const getApprovalTier = () => {
    if (!request.hod_approval && !request.finance_approval) {
      return "hod"; // First tier - HOD needs to approve
    } else if (request.hod_approval && !request.finance_approval) {
      return "finance"; // Second tier - Finance needs to approve
    }
    return "completed"; // Both approved
  };

  // Check if request can be edited
  const canEdit = () => {
    return !request.hod_approval && !request.finance_approval;
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 z-[9998]"
          onClick={onClose}
        ></div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative z-[9999] inline-block w-full max-w-3xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-3 items-center">
              <div className="p-3 bg-red-600 rounded-xl">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Request Details
                </h3>
                <p className="text-sm text-gray-500">Post-Paid SMS Units Request</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 transition-colors hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Status Badge */}
          <div className="mb-6">{getStatusBadge()}</div>

          <div className="space-y-6">
            {/* Customer Information */}
            <div className="p-6 bg-gray-50 rounded-xl">
              <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                <User className="w-5 h-5 text-red-600" />
                Customer Information
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500">Customer Name</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {request.customer_name || "Not provided"}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="flex gap-2 items-center text-xs font-medium text-gray-500">
                    <Mail className="w-4 h-4" />
                    Email Address
                  </p>
                  <p className="text-sm text-gray-900 break-all">
                    {request.customer_email || "Not provided"}
                  </p>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <p className="flex gap-2 items-center text-xs font-medium text-gray-500">
                    <Hash className="w-4 h-4" />
                    User ID
                  </p>
                  <p className="font-mono text-sm text-gray-900">
                    {request.user_slug || "Not provided"}
                  </p>
                </div>
              </div>
            </div>

            {/* Request Details */}
            <div className="p-6 bg-gray-50 rounded-xl">
              <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                <DollarSign className="w-5 h-5 text-red-600" />
                Request Details
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500">Amount</p>
                  <p className="text-2xl font-bold text-red-600">
                    GH₵{parseFloat(request.amount || 0).toFixed(2)}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500">SMS Units</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {request.sms_units?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500">Price Per Unit</p>
                  <p className="text-lg font-semibold text-gray-900">
                    GH₵{parseFloat(request.per_unit_price || 0).toFixed(4)}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500">Status</p>
                  <p className="text-sm font-semibold text-gray-900 capitalize">
                    {request.status || "pending"}
                  </p>
                </div>
              </div>
            </div>

            {/* Approval Status */}
            <div className="p-6 bg-gray-50 rounded-xl">
              <h4 className="mb-4 text-lg font-semibold text-gray-900">
                Approval Status
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex justify-between items-center p-4 bg-white rounded-lg border border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-gray-700">HOD Approval</p>
                    <p className="text-xs text-gray-500">Head of Department</p>
                  </div>
                  {request.hod_approval ? (
                    <div className="flex gap-2 items-center text-green-600">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-semibold">Approved</span>
                    </div>
                  ) : (
                    <div className="flex gap-2 items-center text-orange-600">
                      <Clock className="w-5 h-5" />
                      <span className="text-sm font-semibold">Pending</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-lg border border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Finance Approval
                    </p>
                    <p className="text-xs text-gray-500">Finance Department</p>
                  </div>
                  {request.finance_approval ? (
                    <div className="flex gap-2 items-center text-green-600">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-semibold">Approved</span>
                    </div>
                  ) : (
                    <div className="flex gap-2 items-center text-orange-600">
                      <Clock className="w-5 h-5" />
                      <span className="text-sm font-semibold">Pending</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Receipt & Timestamp */}
            <div className="p-6 bg-gray-50 rounded-xl">
              <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                <Calendar className="w-5 h-5 text-red-600" />
                Additional Information
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500">Receipt URL</p>
                  {request.receipt_url && request.receipt_url !== "sssss" ? (
                    <a
                      href={request.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex gap-2 items-center text-sm font-medium text-red-600 hover:text-red-700"
                    >
                      <LinkIcon className="w-4 h-4" />
                      View Receipt
                    </a>
                  ) : (
                    <p className="text-sm italic text-gray-400">No receipt provided</p>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500">Created At</p>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {request.created_at
                        ? new Date(request.created_at).toLocaleDateString()
                        : "Unknown"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {request.created_at
                        ? new Date(request.created_at).toLocaleTimeString()
                        : ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 mt-8">
            <FeaturePermission
              permission={POST_PAID_PERMISSIONS.EDIT_REQUEST}
              checkPermission={(admin) => {
                if (!canEdit()) return false;
                return true;
              }}
            >
              {canEdit() && onEdit && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onEdit(request);
                    onClose();
                  }}
                  className="flex gap-2 justify-center items-center px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                >
                  <Edit className="w-4 h-4" />
                  Edit Request
                </motion.button>
              )}
            </FeaturePermission>

            <FeaturePermission
              checkPermission={(admin) => {
                if (getApprovalTier() === "completed") return false;
                return canApproveRequest(admin, request);
              }}
            >
              {getApprovalTier() !== "completed" && onApprove && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onApprove(request)}
                  disabled={approving}
                  className="flex flex-1 gap-2 justify-center items-center px-4 py-3 text-sm font-medium text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Approve {getApprovalTier() === "hod" ? "HOD" : "Finance"}
                    </>
                  )}
                </motion.button>
              )}
            </FeaturePermission>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className={`px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700 ${
                getApprovalTier() === "completed" || !onApprove ? "flex-1" : ""
              }`}
            >
              Close
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RequestDetailsModal;

